/**
 * Wallet QBit SDK - React Hooks
 * QBit 钱包功能的 React Hooks
 */

import { useState, useCallback, useEffect } from 'react';
import { walletQBitApi } from '../services/walletQBitApi';

/**
 * 使用 QBit 钱包功能
 */
export function useWalletQBit() {
  const [portfolio, setPortfolio] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 获取资产组合
  const fetchPortfolio = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await walletQBitApi.getPortfolio();
      setPortfolio(result.data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取所有余额
  const fetchBalances = useCallback(async () => {
    try {
      const result = await walletQBitApi.getAllBalances();
      setBalances(result.data || []);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // 获取充值地址
  const getDepositAddress = useCallback(async (assetId, chain) => {
    try {
      const result = await walletQBitApi.getDepositAddress(assetId, chain);
      return result.data?.address;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // 提现
  const withdraw = useCallback(async (withdrawData) => {
    setLoading(true);
    try {
      const result = await walletQBitApi.withdraw(withdrawData);
      await fetchBalances();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBalances]);

  useEffect(() => {
    fetchPortfolio();
    fetchBalances();
  }, [fetchPortfolio, fetchBalances]);

  return {
    portfolio,
    balances,
    loading,
    error,
    totalValue: portfolio?.total_value || 0,
    fetchPortfolio,
    fetchBalances,
    getDepositAddress,
    withdraw,
  };
}

/**
 * 使用投资产品功能
 */
export function useInvestProducts() {
  const [products, setProducts] = useState([]);
  const [myInvestments, setMyInvestments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 获取投资产品
  const fetchProducts = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const result = await walletQBitApi.getInvestProducts(params);
      setProducts(result.data || []);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取我的投资
  const fetchMyInvestments = useCallback(async () => {
    try {
      const result = await walletQBitApi.getMyInvestments();
      setMyInvestments(result.data || []);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // 申购
  const subscribe = useCallback(async (productId, amount) => {
    setLoading(true);
    try {
      const result = await walletQBitApi.subscribe(productId, amount);
      await fetchMyInvestments();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchMyInvestments]);

  // 赎回
  const redeem = useCallback(async (productId, amount) => {
    setLoading(true);
    try {
      const result = await walletQBitApi.redeem(productId, amount);
      await fetchMyInvestments();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchMyInvestments]);

  useEffect(() => {
    fetchProducts();
    fetchMyInvestments();
  }, [fetchProducts, fetchMyInvestments]);

  return {
    products,
    myInvestments,
    loading,
    error,
    fetchProducts,
    fetchMyInvestments,
    subscribe,
    redeem,
  };
}

